import * as React from 'react'
import moment from 'moment'

import { useQueryAlerts } from '../hooks/useQueryAlerts'
import { AlertsTable } from '../components/AlertsTable'
import { AlertModel } from '@/app/models/alert/alertModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import { Permissions } from '@/app/constants/permissions'
import {
  useTimetable,
  useTimetableStore,
} from '@/modules/timetable/stores/timetableStore'

import { useQueryDrivers } from '../hooks/useQueryDrivers'
import { useQueryVehicles } from '../hooks/useQueryVehicles'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import CreateNewAlertModal from '../modals/CreateNewAlertModal'

type RouteParams = {
  companyId: string
  from?: string
  to?: string
}

export const AlertsListPage: React.FC<RouteComponentProps<RouteParams>> = ({
  match,
}) => {
  const { params } = match
  const companyId = params.companyId

  const { t } = useLocalize()
  const can = usePermissionChecker(companyId)
  const canEditAlerts = can(Permissions.canEditAlerts)

  const [isAlertModalOpen, setIsAlertModalOpen] = React.useState(false)
  const [editedAlert, setEditedAlert] = React.useState<AlertModel | null>(null)

  const {
    onlyVisible,
    filteredTypes,
    dateRange,
    setSelectedAlerts,
    selectedAlerts,
    showOnlyUpComing,
    setAmount,
    setCanEditAlerts,
    showAddAlert,
    setShowAddAlert,
    editedAlert: editedAlertFromStore,
    setEditedAlert: setEditedAlertInStore,
  } = useTimetable()

  const { data: drivers = [] } = useQueryDrivers({
    companyId,
    enabled: canEditAlerts && (isAlertModalOpen || showAddAlert),
  })

  const { data: vehicles = [] } = useQueryVehicles({
    companyId,
    enabled: canEditAlerts && (isAlertModalOpen || showAddAlert),
  })

  React.useEffect(() => {
    setCanEditAlerts(canEditAlerts)
  }, [canEditAlerts, setCanEditAlerts])

  const dateFrom = React.useMemo(() => {
    const s = dateRange?.startDate
    if (s?.year != null && s?.month != null && s?.day != null) {
      return moment({ year: s.year, month: s.month - 1, day: s.day }).startOf(
        'day',
      )
    }
    return moment().subtract(30, 'days')
  }, [
    dateRange?.startDate?.year,
    dateRange?.startDate?.month,
    dateRange?.startDate?.day,
  ])

  const dateTo = React.useMemo(() => {
    const e = dateRange?.endDate
    if (e?.year != null && e?.month != null && e?.day != null) {
      return moment({ year: e.year, month: e.month - 1, day: e.day }).endOf(
        'day',
      )
    }
    return moment()
  }, [
    dateRange?.endDate?.year,
    dateRange?.endDate?.month,
    dateRange?.endDate?.day,
  ])

  // The `upcoming` flag only moves the server's date filter — everything before it
  // (eleven alert models over the whole driver roster) is recomputed identically. So
  // one request covering both windows replaces the pair, and the two buckets are cut
  // out of it below with the very same bounds the server would have applied.
  //
  // Server windows, from AlertRequestHandler:
  //   upcoming=false -> EventLocalDayTime in [dateFrom, dateTo + 1 day]
  //   upcoming=true  -> EventLocalDayTime in [today 00:00, today 00:00 + 1 month]
  // and, when onlyVisible is off, the "not remind anymore" alerts are added on the
  // [dateFrom, dateTo + 1 day] window in BOTH cases — hence the notRemindAnymore
  // branch in the upcoming filter.
  const alertsDayFrom = React.useMemo(
    () => moment(dateFrom).startOf('day'),
    [dateFrom],
  )
  // The server compares against DateTo.AddDays(1), and dateTo is serialised date-only,
  // so the effective bound is the day after the picked end date.
  const alertsDayToExclusive = React.useMemo(
    () => moment(dateTo).startOf('day').add(1, 'day'),
    [dateTo],
  )

  const mergedDateFrom = React.useMemo(() => {
    const today = moment().startOf('day')
    return alertsDayFrom.isBefore(today) ? alertsDayFrom : today
  }, [alertsDayFrom])

  const mergedDateTo = React.useMemo(() => {
    const upcomingEnd = moment().startOf('day').add(1, 'month')
    return alertsDayToExclusive.isAfter(upcomingEnd)
      ? alertsDayToExclusive
      : upcomingEnd
  }, [alertsDayToExclusive])

  const {
    data: alertsData = [],
    isLoading,
    refetch: refetchAlerts,
    error: alertsError,
  } = useQueryAlerts({
    companyId,
    drivers: [],
    dateFrom: mergedDateFrom,
    dateTo: mergedDateTo,
    isDriver: false,
    upcoming: false,
    onlyVisible,
    enabled: true,
  })

  const filterAlertsByType = (alerts: AlertModel[]): AlertModel[] => {
    if (filteredTypes.length === 0) return alerts
    return alerts.filter((a) => filteredTypes.includes(a.type))
  }

  // EventLocalDayTime is the local event time truncated to midnight, which is what the
  // server's window compares; eventTime carries the full local timestamp.
  const eventDay = (a: AlertModel) => moment(a.eventTime).startOf('day')
  const inPickedRange = (a: AlertModel) => {
    const day = eventDay(a)
    return (
      !day.isBefore(alertsDayFrom) && !day.isAfter(alertsDayToExclusive)
    )
  }

  const overdueAlerts = React.useMemo(() => {
    const filtered = filterAlertsByType(alertsData)
    const now = moment()
    return filtered.filter((a) => moment(a.eventTime) <= now && inPickedRange(a))
  }, [alertsData, filteredTypes, alertsDayFrom, alertsDayToExclusive])

  const upcomingAlerts = React.useMemo(() => {
    const filtered = filterAlertsByType(alertsData)
    const now = moment()
    const upcomingFrom = moment().startOf('day')
    const upcomingTo = moment().startOf('day').add(1, 'month')
    return filtered.filter((a) => {
      if (moment(a.eventTime) <= now) return false
      // "Not remind anymore" alerts were never narrowed to the upcoming window.
      if (a.notRemindAnymore) return inPickedRange(a)
      const day = eventDay(a)
      return !day.isBefore(upcomingFrom) && !day.isAfter(upcomingTo)
    })
  }, [alertsData, filteredTypes, alertsDayFrom, alertsDayToExclusive])

  const allAlerts = React.useMemo(() => {
    const source = showOnlyUpComing
      ? upcomingAlerts
      : [...overdueAlerts, ...upcomingAlerts]
    return source.map((a, i) => ({
      ...a,
      index: i,
    }))
  }, [overdueAlerts, upcomingAlerts, showOnlyUpComing])

  const handleSelectAlert = (index: number, checked: boolean) => {
    setSelectedAlerts((prev) => {
      if (checked) {
        return prev.includes(index) ? prev : [...prev, index]
      } else {
        return prev.filter((i) => i !== index)
      }
    })
  }

  const handleSelectAllAlerts = (checked: boolean) => {
    if (checked) {
      setSelectedAlerts(allAlerts.map((a) => a.index))
    } else {
      setSelectedAlerts([])
    }
  }

  const displayContent = allAlerts.length > 0
  const hasError = alertsError
  React.useEffect(() => {
    setAmount(allAlerts.length)
  }, [allAlerts.length])

  React.useEffect(() => {
    useTimetableStore.setState({ alerts: allAlerts })
  }, [allAlerts])

  const closeAlertModal = () => {
    setIsAlertModalOpen(false)
    setEditedAlert(null)
  }

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {/* <CreateEditAlertModal
        isOpen={isAlertModalOpen}
        onClose={closeAlertModal}
        onSave={() => {
          void refetchAlerts()
          closeAlertModal()
        }}
        editedAlert={editedAlert ?? undefined}
        companyId={companyId}
        drivers={drivers}
        vehicles={vehicles}
      /> */}
      {hasError ? (
        <div className="tw-rounded-lg tw-border tw-border-error tw-bg-error/10 tw-shadow-sm">
          <div className="tw-border-b tw-border-error tw-bg-error/20 tw-px-4 tw-py-3">
            <h3 className="tw-text-sm tw-font-semibold tw-text-content-base">
              {t('alerts.error-title')}
            </h3>
          </div>
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <div className="tw-text-center">
              <p className="tw-mb-4 tw-text-content-base">
                {t('alerts.error-message')}
              </p>
              <button
                onClick={() => {
                  refetchAlerts()
                }}
                className="tw-rounded tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90"
              >
                {t('operation.retry')}
              </button>
            </div>
          </div>
        </div>
      ) : isLoading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
          <p className="tw-text-content-base">{t('gridPager.loadingText')}</p>
        </div>
      ) : displayContent ? (
        <>
          <AlertsTable
            alerts={allAlerts}
            selectedAlerts={selectedAlerts}
            onSelectAlert={handleSelectAlert}
            onSelectAll={handleSelectAllAlerts}
            onEdit={() => {}}
            canEdit={canEditAlerts}
            upcoming={false}
          />
        </>
      ) : (
        <div className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-h-full tw-shadow-sm">
          <div className="tw-border-b tw-border-base-300 tw-bg-content-primary tw-px-4 tw-py-3">
            <h3 className="tw-text-sm tw-font-semibold tw-text-content-base">
              {t('alertList.no-alerts')}
            </h3>
          </div>
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <p className="tw-text-content-base tw-opacity-70">
              {t('alerts.empty-results')}
            </p>
          </div>
        </div>
      )}
      {showAddAlert && (
        <CreateNewAlertModal
          handleClose={() => {
            setShowAddAlert(false)
            setEditedAlertInStore(null)
          }}
          onSave={() => {
            void refetchAlerts()
          }}
          editedAlert={editedAlertFromStore}
          companyId={companyId}
          drivers={drivers}
          vehicles={vehicles}
        />
      )}
    </section>
  )
}
